import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { api } from '../../services/api-client';
import { useToast } from '../../contexts/ToastContext';
import {
  buildWeekOptions,
  defaultWeekIndex,
  markRecommendedWeek,
  selectableCycles,
  PreviousCycle,
  WeekOption
} from './previousCycleWeeks';

interface UseCycleFromPreviousWeekArgs {
  visible: boolean;
  clientId: number | string;
  onDone: () => void;
}

/**
 * #23 — tercer camino del wizard de creación de ciclos: reutilizar una semana
 * de un ciclo anterior del MISMO alumno.
 *
 * Va en un hook aparte y no dentro de `useCreateTrainingCycleDialog` (que ya
 * tiene 400 líneas manejando los otros dos flujos) para no mezclar estados.
 */
export function useCycleFromPreviousWeek({ visible, clientId, onDone }: UseCycleFromPreviousWeekArgs) {
  const intl = useIntl();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cycles, setCycles] = useState<PreviousCycle[]>([]);
  const [sourceCycleId, setSourceCycleId] = useState<number | null>(null);
  const [sourceWeekIndex, setSourceWeekIndex] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [durationInWeeks, setDurationInWeeks] = useState<number>(4);

  // Solo los ciclos que tienen algo cargado: uno vacío no sirve como origen.
  const availableCycles = useMemo(() => selectableCycles(cycles), [cycles]);

  const selectedCycle = useMemo(
    () => availableCycles.find((c) => c.id === sourceCycleId) ?? null,
    [availableCycles, sourceCycleId]
  );

  const weekOptions: WeekOption[] = useMemo(
    () => markRecommendedWeek(buildWeekOptions(selectedCycle)),
    [selectedCycle]
  );

  useEffect(() => {
    if (!visible || !clientId) return;

    setLoading(true);
    api.workout
      .fetchTrainingCyclesByClient(Number(clientId))
      .then(({ data }) => setCycles(data ?? []))
      .catch(() => setCycles([]))
      .finally(() => setLoading(false));
  }, [visible, clientId]);

  // Reset al cerrar, para que reabrir no arrastre la selección anterior.
  useEffect(() => {
    if (visible) return;
    setSourceCycleId(null);
    setSourceWeekIndex(null);
    setName('');
    setDurationInWeeks(4);
    setStartDate(new Date());
  }, [visible]);

  /** Al elegir un ciclo se preselecciona su última semana (#23). */
  const selectCycle = useCallback(
    (cycleId: number) => {
      setSourceCycleId(cycleId);
      const cycle = availableCycles.find((c) => c.id === cycleId) ?? null;
      setSourceWeekIndex(defaultWeekIndex(buildWeekOptions(cycle)));
    },
    [availableCycles]
  );

  const canSubmit = Boolean(
    name.trim() && startDate && durationInWeeks > 0 && sourceCycleId && sourceWeekIndex !== null
  );

  const submit = useCallback(async () => {
    if (!canSubmit || !startDate || sourceCycleId === null || sourceWeekIndex === null) return;

    try {
      setSaving(true);
      await api.workout.createCycleFromPreviousWeek({
        name: name.trim(),
        clientId: Number(clientId),
        startDate,
        durationInWeeks,
        sourceCycleId,
        sourceWeekIndex
      });
      showToast(
        'success',
        intl.formatMessage({ id: 'common.success' }),
        intl.formatMessage({
          id: 'cycle.wizard.fromPreviousCycle.success',
          defaultMessage: 'Ciclo creado a partir de la semana elegida'
        })
      );
      onDone();
    } catch (error) {
      showToast('error', intl.formatMessage({ id: 'common.error' }), (error as Error).message);
    } finally {
      setSaving(false);
    }
  }, [canSubmit, name, clientId, startDate, durationInWeeks, sourceCycleId, sourceWeekIndex, showToast, intl, onDone]);

  return {
    loading,
    saving,
    availableCycles,
    selectedCycle,
    weekOptions,
    sourceCycleId,
    sourceWeekIndex,
    setSourceWeekIndex,
    selectCycle,
    name,
    setName,
    startDate,
    setStartDate,
    durationInWeeks,
    setDurationInWeeks,
    canSubmit,
    submit,
    intl
  };
}
